import { useQueryClient } from '@tanstack/react-query'

import { getGetPartnerQueryKey, getListPartnersQueryKey, useUpdatePartner } from '@/api/gen'
import { useMutationErrorHandler } from '@/hooks/use-mutation-error-handler'
import { extractValidationDetail } from '@/utils/extract-validation-detail'

export const usePartnerUpdate = (
  partnerId: string,
  onSuccess: () => void,
  // Given a failure, hangs any field-scoped 422 errors somewhere (e.g. onto a
  // form) and returns `true` when it consumed them — the inline alert then only
  // handles the non-field failures.
  mapFieldErrors?: (error: unknown) => boolean,
) => {
  const queryClient = useQueryClient()
  // Surface a server 422 field-error message (e.g. an invalid IBAN checksum on
  // the payout-method write) as the fallback authority beyond client validation.
  const { hasError, detail, onError, clearError } = useMutationErrorHandler(
    'updatePartner failed: ',
    {
      extractDetail: extractValidationDetail,
    },
  )

  const mutation = useUpdatePartner({
    mutation: {
      onMutate: clearError,
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetPartnerQueryKey(partnerId) })
        void queryClient.invalidateQueries({ queryKey: getListPartnersQueryKey() })
        onSuccess()
      },
      onError: (error) => {
        if (mapFieldErrors?.(error) !== true) {
          onError(error)
        }
      },
    },
  })

  return { mutation, hasError, detail }
}
